/**
 * CartDrawer — sliding right-side drawer that shows all cart items + a
 * "Checkout" button that fires the CartContext.checkout() flow (redirects
 * the browser to Stripe Checkout).
 */
import React, { useContext, useState } from 'react';
import { ShoppingCart, X, Trash2, Minus, Plus, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

const CartDrawer = () => {
  const {
    items, count, totalCents,
    updateQuantity, removeItem, clear,
    drawerOpen, setDrawerOpen,
    checkout, checkingOut,
  } = useCart();
  const { user } = useContext(AuthContext);
  const [email, setEmail] = useState('');

  if (!drawerOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[130] flex justify-end"
      role="dialog"
      aria-label="Shopping cart"
      data-testid="cart-drawer"
    >
      <button
        type="button"
        className="absolute inset-0 bg-navy/50 backdrop-blur-sm"
        onClick={() => setDrawerOpen(false)}
        data-testid="cart-drawer-backdrop"
        aria-label="Close cart"
      />
      <aside className="relative h-full w-full max-w-md bg-background shadow-2xl border-l border-border flex flex-col animate-in slide-in-from-right duration-300">
        <header className="flex items-center justify-between px-5 h-16 border-b border-border">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-lg font-extrabold tracking-tight">
              Your cart <span className="text-foreground/60 font-bold">· {count}</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-primary/10 text-foreground/70 hover:text-foreground transition"
            data-testid="cart-drawer-close"
            aria-label="Close cart"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16" data-testid="cart-empty">
              <ShoppingCart className="w-10 h-10 text-foreground/30 mx-auto mb-4" />
              <p className="text-foreground/60">Your cart is empty.</p>
            </div>
          )}
          {items.map((i) => (
            <div key={i.key} className="pro-card p-4" data-testid={`cart-line-${i.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-primary">
                    {i.kind === 'event_ticket' ? 'Event ticket' : 'Product'}
                  </div>
                  <div className="font-heading font-extrabold text-foreground tracking-tight mt-0.5 truncate">
                    {i.name}
                  </div>
                  {i.meta?.date_label && (
                    <div className="text-xs text-foreground/60 mt-0.5">{i.meta.date_label}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(i.key)}
                  className="h-8 w-8 rounded-full hover:bg-destructive/10 text-foreground/50 hover:text-destructive transition inline-flex items-center justify-center"
                  data-testid={`cart-remove-${i.key}`}
                  aria-label="Remove item"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="inline-flex items-center gap-1 border border-border rounded-full">
                  <button
                    type="button"
                    onClick={() => updateQuantity(i.key, i.quantity - 1)}
                    className="h-8 w-8 rounded-full hover:bg-primary/10 inline-flex items-center justify-center"
                    data-testid={`cart-qty-dec-${i.key}`}
                    aria-label="Decrease quantity"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="w-6 text-center font-bold text-sm" data-testid={`cart-qty-${i.key}`}>{i.quantity}</span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(i.key, i.quantity + 1)}
                    className="h-8 w-8 rounded-full hover:bg-primary/10 inline-flex items-center justify-center"
                    data-testid={`cart-qty-inc-${i.key}`}
                    aria-label="Increase quantity"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
                <div className="font-heading font-extrabold text-foreground tracking-tight">
                  {money((i.unit_amount_cents || 0) * i.quantity)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <footer className="border-t border-border p-5 space-y-4">
            {!user && (
              <div>
                <label className="text-xs font-extrabold uppercase tracking-[0.14em] text-foreground/60">
                  Email for receipt (optional)
                </label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="cart-guest-email"
                  className="mt-2"
                />
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-foreground/70">Subtotal</span>
              <span className="font-heading text-2xl font-extrabold text-foreground tracking-tight" data-testid="cart-subtotal">
                {money(totalCents)}
              </span>
            </div>
            <Button
              size="lg"
              onClick={() => checkout({ email })}
              disabled={checkingOut}
              className="w-full rounded-full py-6 font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="cart-checkout-btn"
            >
              {checkingOut ? 'Redirecting…' : (<><span>Checkout</span><ArrowRight className="ml-2 w-4 h-4" /></>)}
            </Button>
            <button
              type="button"
              onClick={clear}
              className="w-full text-xs text-foreground/50 hover:text-destructive transition"
              data-testid="cart-clear-btn"
            >
              Clear cart
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
};

export const CartButton = ({ className = '' }) => {
  const { count, setDrawerOpen } = useCart();
  return (
    <button
      type="button"
      onClick={() => setDrawerOpen(true)}
      className={`relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-primary/10 text-foreground/70 hover:text-foreground transition ${className}`}
      data-testid="cart-button"
      aria-label="Open cart"
    >
      <ShoppingCart className="w-4 h-4" />
      {count > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-extrabold inline-flex items-center justify-center"
          data-testid="cart-count-badge"
        >
          {count}
        </span>
      )}
    </button>
  );
};

export default CartDrawer;
